import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const BUILD_ID =
  typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "dev";

const CHECK_INTERVAL_MS = 60 * 1000;

async function fetchRemoteBuildId() {
  const res = await fetch(`/version.json?_=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.version ? String(data.version) : null;
}

/** בודק אם יש גרסה חדשה בשרת. לא מרענן אוטומטית כדי למנוע לולאת reload ב-PWA מותקן. */
export async function checkForAppUpdate() {
  if (import.meta.env.DEV) return false;
  try {
    const remote = await fetchRemoteBuildId();
    if (remote && remote !== BUILD_ID) {
      return true;
    }
  } catch {
    /* offline / network error */
  }
  return false;
}

export function registerServiceWorkerUpdates(registerSW) {
  if (import.meta.env.DEV) return;

  registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;

      const ping = () => registration.update().catch(() => {});

      ping();
      setInterval(ping, CHECK_INTERVAL_MS);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") ping();
      });
    },
    onOfflineReady() {
      /* offline ready */
    },
  });
}

/** Hook — בדיקת גרסה בטעינה, במעבר דף, ובחזרה לטאב */
export function useAppUpdate() {
  const location = useLocation();
  const checkingRef = useRef(false);

  useEffect(() => {
    if (import.meta.env.DEV) return;

    const runCheck = async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;
      try {
        await checkForAppUpdate();
      } finally {
        checkingRef.current = false;
      }
    };

    runCheck();

    const onVisible = () => {
      if (document.visibilityState === "visible") runCheck();
    };

    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(runCheck, CHECK_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [location.pathname]);
}

export { BUILD_ID };

/** תצוגה בתפריט — מספר גרסה + תאריך ושעת העלאה (DDMMYYHHmm) */
export function formatBuildIdLabel(buildId = BUILD_ID) {
  if (import.meta.env.DEV) return "dev";
  const raw = String(buildId || "").trim();
  if (!raw) return "—";
  if (/^\d+$/.test(raw)) return raw;
  return raw;
}
